export type ErrorMessageVars = Record<string, string | number>;
export type ErrorsCatalog = Record<string, string>;

function interpolateErrorMessage(
  template: string,
  vars?: ErrorMessageVars,
): string {
  if (!vars) return template;
  let out = template;
  for (const [k, v] of Object.entries(vars)) {
    out = out.split(`{${k}}`).join(String(v));
  }
  return out;
}

function extractErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const code = (error as { errorCode?: unknown }).errorCode;
  return typeof code === 'string' ? code : undefined;
}

function extractErrorDetails(error: unknown): Record<string, unknown> | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const details = (error as { details?: unknown }).details;
  return details && typeof details === 'object'
    ? (details as Record<string, unknown>)
    : undefined;
}

/**
 * Pure resolver - takes an error and the errors catalog, returns the localized
 * user-facing message.
 *
 * Resolution order:
 *  1. error.errorCode is in the catalog → translate it (with details/vars interpolated).
 *  2. error.details.message is a non-empty string → use it as-is.
 *  3. catalog.DEFAULT.
 *
 * Accepts any error-shaped object with an optional `errorCode` string field
 * (ApiError instances, but also synthetic `{ errorCode, details }` shapes -
 * useful for cases where only the code survived through context state).
 */
export function resolveErrorMessage(
  error: unknown,
  catalog: ErrorsCatalog,
  vars?: ErrorMessageVars,
): string {
  const code = extractErrorCode(error);
  const details = extractErrorDetails(error);
  const allVars = { ...(details as ErrorMessageVars), ...vars };

  if (code && typeof catalog[code] === 'string') {
    return interpolateErrorMessage(catalog[code], allVars);
  }

  const detailMessage = details?.message;
  if (typeof detailMessage === 'string' && detailMessage.length > 0) {
    return detailMessage;
  }

  return catalog.DEFAULT;
}
